import { Info } from 'lucide-react';
import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { RadioGroup, RadioGroupItem } from '@/Domain/components/Shared/RadioGroup/RadioGroup';

export const WHATSAPP_NUMBER_SOURCES = ['new_number', 'business_app', 'personal_app'] as const;
export type WhatsAppNumberSource = (typeof WHATSAPP_NUMBER_SOURCES)[number];

const BOLD_LEAD = <strong className="font-semibold" />;

const NUMBER_SOURCE_NOTES = {
  in_use: {
    testId: 'whatsapp-number-source-note',
    title: 'in_use_title',
    paragraphs: ['in_use_delete_account', 'in_use_keep_chats'],
  },
  coexistence: {
    testId: 'whatsapp-number-source-coexistence-note',
    title: 'coexistence_title',
    paragraphs: ['coexistence_connect', 'coexistence_pause'],
  },
} as const;

type NumberSourceNote = (typeof NUMBER_SOURCE_NOTES)[keyof typeof NUMBER_SOURCE_NOTES];

const noteFor = (
  value: WhatsAppNumberSource | null,
  coexistenceEnabled: boolean,
): NumberSourceNote | null => {
  if (value === 'business_app') {
    return coexistenceEnabled ? NUMBER_SOURCE_NOTES.coexistence : NUMBER_SOURCE_NOTES.in_use;
  }
  return value === 'personal_app' ? NUMBER_SOURCE_NOTES.in_use : null;
};

interface ConnectNumberSourceChoiceProps {
  value: WhatsAppNumberSource | null;
  onChange: (value: WhatsAppNumberSource) => void;
  disabled?: boolean;
  coexistenceEnabled?: boolean;
}

export const ConnectNumberSourceChoice = ({
  value,
  onChange,
  disabled = false,
  coexistenceEnabled = false,
}: ConnectNumberSourceChoiceProps) => {
  const { t } = useTranslation(['integrations']);
  const id = useId();
  const note = noteFor(value, coexistenceEnabled);

  return (
    <div className="flex flex-col gap-2 pb-1" data-testid="whatsapp-number-source">
      <p id={`${id}-label`} className="text-sm font-medium text-foreground">
        {t('whatsapp_phone_numbers.setup.number_source.label')}
      </p>
      <RadioGroup
        value={value ?? ''}
        onValueChange={(next) => onChange(next as WhatsAppNumberSource)}
        disabled={disabled}
        aria-labelledby={`${id}-label`}
      >
        {WHATSAPP_NUMBER_SOURCES.map((source) => (
          <div key={source} className="flex items-start gap-2">
            <RadioGroupItem
              id={`${id}-${source}`}
              value={source}
              className="peer mt-0.5"
              data-testid={`whatsapp-number-source-${source}`}
            />
            <Label htmlFor={`${id}-${source}`} className="cursor-pointer font-normal leading-snug">
              {t(`whatsapp_phone_numbers.setup.number_source.${source}`)}
            </Label>
          </div>
        ))}
      </RadioGroup>
      {note && (
        <Alert
          key={note.testId}
          variant="info"
          role="status"
          className="text-foreground [&>svg]:text-info"
          data-testid={note.testId}
        >
          <Info className="size-4" />
          <AlertTitle>{t(`whatsapp_phone_numbers.setup.number_source.${note.title}`)}</AlertTitle>
          <AlertDescription className="flex flex-col gap-1.5 text-xs">
            {note.paragraphs.map((paragraph) => (
              <p key={paragraph}>
                <Trans
                  t={t}
                  i18nKey={`whatsapp_phone_numbers.setup.number_source.${paragraph}`}
                  components={[BOLD_LEAD]}
                />
              </p>
            ))}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
